import { Fragment, useMemo, type FC } from 'react';

import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type Audit } from '#packages/data-collection/models/audit';
import { isNil } from '#packages/data-collection/libs/lodash';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { routes } from '#apps/client-web/app/routes';
import { ThrowError } from '#apps/client-web/app/components/throw-error';

import { type AuditListPageViewModel } from './audit-list-page.view-model';
import { AuditListPageRenderer } from './audit-list-page.renderer';

export type AuditListPageContainerProps =
  ViewModelProps<AuditListPageViewModel> & LoggingContext;

export const AuditListPageContainer: FC<AuditListPageContainerProps> = ({
  viewModel,

  logger,
}) => {
  const { auditListQuery, onCreate } = viewModel;

  const auditsLinkMap = useMemo(() => {
    if (isNil(auditListQuery.data)) {
      logger?.debug('No audit data available to create auditsLinkMap');
      return {};
    }

    const [audits] = auditListQuery.data;

    // Memoize the mapping of audit links to audit objects
    return audits.reduce(
      (map, audit) => {
        map[routes.audit.getDetailsPath(audit.id)] = audit;
        return map;
      },
      {} as Record<string, Audit>,
    );
  }, [auditListQuery.data]);

  // return <AuditListPageRenderer auditsLinkMap={auditsLinkMap} />;
  const isLoading = auditListQuery.isLoading;
  const isEmpty =
    auditListQuery.isSuccess && Object.keys(auditsLinkMap).length === 0;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {/* Loading */}
      {isLoading && <LoadingPage />}

      {/* Empty */}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}

      {/* Data */}
      {hasData && <AuditListPageRenderer auditsLinkMap={auditsLinkMap} />}

      {/* FAB */}
      {hasData && <Fab onClick={onCreate} />}

      {/* Error */}
      {auditListQuery.isError && <ThrowError error={auditListQuery.error} />}
    </Fragment>
  );
};
